// SVG icon fragments — each rendered as a <g> at given (cx, cy).

export function SunIcon({ cx, cy, color }: { cx: number; cy: number; color: string }) {
  return (
    <g transform={`translate(${cx}, ${cy})`} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round">
      <circle cx="0" cy="0" r="6" fill={color} fillOpacity="0.4" stroke="none"/>
      <circle cx="0" cy="0" r="6"/>
      <line x1="0" y1="-10" x2="0" y2="-8"/><line x1="0" y1="8" x2="0" y2="10"/>
      <line x1="-10" y1="0" x2="-8" y2="0"/><line x1="8" y1="0" x2="10" y2="0"/>
      <line x1="-7" y1="-7" x2="-5.6" y2="-5.6"/><line x1="5.6" y1="-7" x2="7" y2="-5.6"/>
      <line x1="-7" y1="7" x2="-5.6" y2="5.6"/><line x1="5.6" y1="7" x2="7" y2="5.6"/>
    </g>
  );
}

export function GridIcon({ cx, cy, color }: { cx: number; cy: number; color: string }) {
  return (
    <g transform={`translate(${cx}, ${cy})`} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round">
      <path d="M -9 -2 L 9 -2"/>
      <path d="M -7 -2 L -7 5 a 7 7 0 0 0 14 0 L 7 -2"/>
      <line x1="-3" y1="-7" x2="-3" y2="-2"/><line x1="3" y1="-7" x2="3" y2="-2"/>
    </g>
  );
}

export function HouseIcon({ cx, cy, color }: { cx: number; cy: number; color: string }) {
  return (
    <g transform={`translate(${cx}, ${cy})`} stroke={color} strokeWidth="1.7" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M -11 1 L 0 -10 L 11 1 L 11 9 L -11 9 Z"/>
      <line x1="-3" y1="9" x2="-3" y2="3"/><line x1="3" y1="9" x2="3" y2="3"/>
      <path d="M -3 3 L 3 3"/>
    </g>
  );
}

export function ShieldHouseIcon({ cx, cy, color }: { cx: number; cy: number; color: string }) {
  return (
    <g transform={`translate(${cx}, ${cy})`} stroke={color} strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <path d="M -11 1 L 0 -10 L 11 1 L 11 9 L -11 9 Z"/>
      <path d="M -4 3 a 4 4 0 0 1 8 0"/>
      <path d="M 0 -4 L 0 -8"/>
    </g>
  );
}

/**
 * SOC voor weergave: de verrijkte (pack-aware) waarde, dan de rauwe als fallback
 * voor oude snapshots, en anders `null` = onbekend. Alleen 0-100 telt als meting,
 * dezelfde bereiktoets als `usable_pack_soc` in battery.py; een glitchwaarde als
 * 255 of 65535 is net zo onbekend als een ontbrekend veld. Zonder
 * die toets werd een sentinel via de clamp een groene "100%", en een ontbrekend
 * veld via `?? 0` een rode "0%" — beide niet te onderscheiden van een echte stand
 * (wachtrij #336).
 */
export function soc_of_onbekend(verrijkt: unknown, rauw: unknown): number | null {
  for (const v of [verrijkt, rauw]) {
    if (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 100) return v;
  }
  return null;
}

export function BatteryIcon({ cx, cy, socPct }: { cx: number; cy: number; socPct?: number | null }) {
  // SOC-driven fill: inner rect width scales 0..14 met SOC 0..100. Color band
  // volgt operationele zones — rood <20 (kritiek, dicht bij floor), oranje
  // 20-50 (laag, monitoren), groen >=50 (gezond). Onbekend (null) is een eigen
  // toestand: grijze omtrek, geen vulling, "—". Nooit `?? 0`: dat tekent een
  // ontbrekende meting als een kritiek lege accu.
  const known = soc_of_onbekend(socPct, null);
  const soc = known ?? 0;
  const state = known === null ? 'unknown' : soc < 20 ? 'critical' : soc < 50 ? 'low' : 'healthy';
  const color = known === null ? '#94a3b8' : soc < 20 ? '#ef4444' : soc < 50 ? '#f97316' : '#22c55e';
  const fillW = (14 * soc) / 100;
  // Body rect spans x[-12,10], y[-6,8] → centrum (-1, 1). Tekst-fill is dark
  // grey + paint-order=stroke laat de witte halo de tekst leesbaar houden over
  // zowel het lege als het gevulde deel van de battery-cell.
  return (
    <g transform={`translate(${cx}, ${cy})`} fill="none" strokeLinecap="round" strokeLinejoin="round" data-soc-state={state}>
      <rect x="-12" y="-6" width="22" height="14" rx="2" stroke={color} strokeWidth="1.6"/>
      <rect x="10" y="-3" width="3" height="8" rx="1" fill={color}/>
      {fillW > 0 && <rect x="-9" y="-3" width={fillW} height="8" rx="0.5" fill={color}/>}
      <text
        x="-1" y="1" textAnchor="middle" dominantBaseline="central"
        fontSize="7" fontWeight="700" fill="#1f2937"
        stroke="#ffffff" strokeWidth="2" paintOrder="stroke"
      >
        {known === null ? '—' : `${Math.round(soc)}%`}
      </text>
    </g>
  );
}

export function InverterIcon({ cx, cy, color }: { cx: number; cy: number; color: string }) {
  return (
    <g transform={`translate(${cx}, ${cy})`} stroke={color} strokeWidth="1.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
      <rect x="-14" y="-12" width="28" height="20" rx="3"/>
      <path d="M -8 -3 L 0 -3 L 0 3 L 8 3"/>
      <line x1="-14" y1="-1" x2="-9" y2="-1"/><line x1="9" y1="1" x2="14" y2="1"/>
    </g>
  );
}
